import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ClientProviderSelect from "@/components/ui/client-provider-select";
import CurrencyInput from "@/components/ui/currency-input";
import DateField from "@/components/ui/date-field";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SubmitButton } from "@/components/ui/submit-button";
import { Textarea } from "@/components/ui/textarea";
import VehicleSelect from "@/components/ui/vehicle-select";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { errorHandler } from "@/lib/utils";
import { repairService } from "@/services/api/repairs";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { useEffect } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";

const formSchema = z.object({
  vehicle_id: z.number().min(1, "Selecione um veículo"),
  client_provider_id: z.number().nullable(),
  description: z.string().min(1, "Descrição é obrigatória"),
  odometer: z.coerce
    .number({
      message: "Odômetro deve ser um número",
    })
    .nullable(),
  invoice_number: z.string().nullable(),
  repair_date: z
    .string({
      message: "Data é obrigatória",
    })
    .date("Data inválida"),
  amount: z.number().min(0, "Valor deve ser maior ou igual a zero"),
});

type FormValues = z.infer<typeof formSchema>;

const RepairForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    // ponytail: resolvers 5 types the raw input apart from the parsed
    // output; the form keeps the parsed type, as it did on resolvers 3.
    resolver: zodResolver(formSchema) as Resolver<FormValues>,
    defaultValues: {
      vehicle_id: 0,
      client_provider_id: null,
      description: "",
      odometer: null,
      invoice_number: "",
      repair_date: format(new Date(), "yyyy-MM-dd"),
      amount: 0,
    },
  });

  useEffect(() => {
    const loadRepair = async () => {
      if (id) {
        try {
          const response = await repairService.show(Number(id));
          const repairData = response.data;

          form.reset({
            vehicle_id: repairData.vehicle_id,
            client_provider_id: repairData.client_provider_id,
            description: repairData.description,
            odometer: repairData.odometer
              ? Math.floor(repairData.odometer)
              : undefined,
            invoice_number: repairData.invoice_number,
            repair_date: repairData.repair_date,
            amount: Number(repairData.amount),
          });
        } catch (err) {
          errorHandler(err, "Erro ao carregar reparo");
        }
      }
    };

    loadRepair();
  }, [id, form.reset, navigate, form]);

  const { handleSubmit, loading } = useFormSubmit({
    id,
    createService: repairService.create,
    updateService: repairService.update,
    onSuccess: () => navigate("/repairs"),
  });

  const onSubmit = async (values: FormValues) => {
    await handleSubmit(values);
  };

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>{id ? "Editar Reparo" : "Novo Reparo"}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="vehicle_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Veículo</FormLabel>
                  <VehicleSelect
                    value={field.value}
                    onChange={field.onChange}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={5}
                      className="dark:border-white"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="odometer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Odômetro</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="invoice_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nota Fiscal</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DateField
              form={form}
              label="Data"
              name="repair_date"
              className="flex flex-col"
              placeholder="Selecione uma data"
            />

            <FormField
              control={form.control}
              name="client_provider_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Fornecedor</FormLabel>
                  <ClientProviderSelect
                    value={field.value ?? undefined}
                    onChange={field.onChange}
                    addEmptyOption={false}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Valor (R$)</FormLabel>
                  <FormControl>
                    <CurrencyInput
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/repairs")}
              >
                Cancelar
              </Button>
              <SubmitButton loading={loading} />
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default RepairForm;
